// framework/client/helpers/MessagingClient/ContactInfoModal.js
import { ComponentBase } from "../../../core/ComponentBase.js";
import { createAvatarElement } from "../avatarHelper.js";

export class ContactInfoModal extends ComponentBase {
    static styles = `
        .contact-info-modal .modal-dialog {
            max-width: 450px; 
        }
        .contact-info-modal .modal-body {
            text-align: center; /* Center content like avatar */
        }
        .contact-info-avatar {
            width: 120px;
            height: 120px;
            border-radius: 50%;
            object-fit: cover;
            margin: 0 auto 1rem auto; /* Center and add bottom margin */
            display: block;
            border: 3px solid var(--bs-border-color);
        }
        .contact-info-details h5 { /* Display Name */
            margin-bottom: 0.25rem;
            font-weight: 500;
        }
        .contact-info-details .text-muted { /* Contact ID, Type */
            font-size: 0.9rem;
        }
        .contact-channels-list {
            list-style: none;
            padding-left: 0;
            margin-top: 1rem;
            text-align: left; /* Align channel details to the left */
        }
        .contact-channels-list li {
            padding: 0.3rem 0;
            font-size: 0.9rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
        }
        .contact-channels-list li:last-child {
            border-bottom: none;
        }
        .contact-channels-list .channel-type {
            font-weight: 500;
            color: var(--bs-emphasis-color);
        }
        .contact-channels-list .channel-identifier {
            color: var(--bs-secondary-color);
        }
        .contact-info-modal .modal-footer .btn {
            flex-grow: 1; /* Make buttons take equal width if multiple */
        }
    `;

    constructor(config) { // Expects { appClient, instanceId }
        super(config);
        this.contactId = null;
        this.projectId = null;
        this.contactData = null;

        this.modalElement = null;
        this.modalInstance = null;
        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
        this.createModalElement();
    }

    createModalElement() {
        this.modalElement = document.createElement('div');
        this.modalElement.className = 'modal fade contact-info-modal';
        this.modalElement.id = `${this.config.instanceId}-modal`;
        this.modalElement.setAttribute('tabindex', '-1');
        this.modalElement.setAttribute('aria-labelledby', `${this.config.instanceId}-modalLabel`);
        this.modalElement.setAttribute('aria-hidden', 'true');

        // Basic structure, content will be populated by renderModalContent
        this.modalElement.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="${this.config.instanceId}-modalLabel" data-i18n="Contact Information">Contact Information</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body" id="${this.config.instanceId}-modal-body">
                        <!-- Contact details will be rendered here -->
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" data-i18n="Close">Close</button>
                        <!-- "Start Chat" button will be added dynamically if applicable -->
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(this.modalElement);
        this.modalInstance = new bootstrap.Modal(this.modalElement);
        this.modalElement.addEventListener('hidden.bs.modal', () => {
            this.contactData = null; // Clear data when modal is hidden
            this.modalElement.querySelector(`#${this.config.instanceId}-modal-body`).innerHTML = ''; // Clear body
            this.emit('closed');
        });
    }

async show(contactId, projectId) {
        if (!contactId || !projectId) {
            this.log.error("Cannot show ContactInfoModal: contactId or projectId missing.");
            return;
        }
        this.contactId = contactId; // Set new contactId
        this.projectId = projectId; // Set new projectId

        // Ensure modal body is cleared or shows loading before fetching new data
        const modalBody = this.modalElement.querySelector(`#${this.config.instanceId}-modal-body`);
        modalBody.innerHTML = `<p>${this.t('Loading contact details...')}</p>`; // Show loading
        this.appClient.tx.applyTranslations(modalBody);


        await this.fetchContactDetails(); // Await fetching new details
        
        // Now that this.contactData is updated (or null if error), render the content
        this.renderModalContent(); 
        
        this.modalInstance.show();
    }

    hide() {
        this.modalInstance.hide();
    }

    async fetchContactDetails() {
        this.contactData = null; // Clear previous data
        try {
            this.contactData = await this.appClient.rpcClient.call('MessagingContactsRpc.findByContactId', 
                { contactId: this.contactId }, 
                { projectId: this.projectId }
            );
        } catch (error) {
            this.log.error(`Error fetching contact details for ${this.contactId}:`, error);
            this.appClient.notifier.error("Failed to load contact information.");
            // Optionally set contactData to an error state or leave null
        }
    }

    renderModalContent() {
        const modalBody = this.modalElement.querySelector(`#${this.config.instanceId}-modal-body`);
        const modalFooter = this.modalElement.querySelector('.modal-footer');
        modalBody.innerHTML = ''; // Clear previous
        
        // Remove existing "Start Chat" button if any
        const existingStartChatBtn = modalFooter.querySelector('.start-chat-btn');
        if (existingStartChatBtn) existingStartChatBtn.remove();

        if (!this.contactData) {
            modalBody.innerHTML = `<p>${this.t('Loading contact details...')}</p>`;
            this.appClient.tx.applyTranslations(modalBody);
            return;
        }

        const avatarElement = createAvatarElement({
            displayName: this.contactData.displayName,
            avatarUrl: this.contactData.avatarUrl, // Relative path
            sizeClass: 'large', // Or another appropriate size class
            additionalClasses: ['contact-info-avatar'] // Keep specific class
        });

        modalBody.innerHTML = `
            <div class="contact-info-details">
            ${avatarElement.outerHTML}
                <h5>${this.contactData.displayName}</h5>
                <p class="text-muted" data-i18n="Contact ID">Contact ID: ${this.contactData.contactId}</p>
                <p class="text-muted" data-i18n="Type">Type: ${this.t(this.contactData.contactType.replace('_', ' '))}</p> 
            </div>
            <h6 class="mt-3" data-i18n="Channels">Channels</h6>
            <ul class="contact-channels-list">
                ${this.contactData.channels && this.contactData.channels.length > 0
                    ? this.contactData.channels.map(ch => `
                        <li>
                            <span class="channel-type">${this.t(ch.channelType.replace('_', ' '))}:</span>
                            <span class="channel-identifier">${ch.identifier}</span>
                            ${ch.isPreferred ? ` <span class="badge bg-success-subtle text-success-emphasis rounded-pill" data-i18n="Preferred">${this.t('Preferred')}</span>` : ''}
                        </li>`).join('')
                    : `<li class="text-muted" data-i18n="No channels listed.">${this.t('No channels listed.')}</li>`
                }
            </ul>
        `;

        // Add "Start Chat" button if it's not the current user's own contact info
        const myContactId = this.appClient.getSharedData('myMessagingContactId');
        if (this.contactData.contactId !== myContactId) {
            const startChatButton = document.createElement('button');
            startChatButton.type = 'button';
            startChatButton.className = 'btn btn-primary start-chat-btn';
            startChatButton.textContent = this.t('Start Chat');
            startChatButton.setAttribute('data-i18n', 'Start Chat');
            startChatButton.addEventListener('click', () => {
                this.emit('startChatWithContact', { contactId: this.contactData.contactId, displayName: this.contactData.displayName });
                this.hide();
            });
            // Prepend to footer so it appears before "Close"
            modalFooter.insertBefore(startChatButton, modalFooter.firstChild);
        }
        this.appClient.tx.applyTranslations(this.modalElement);
    }

    destroy() {
        if (this.modalElement) {
            this.modalInstance?.dispose();
            this.modalElement.remove();
        }
        super.destroy();
    }
}